import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import HttpClientV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import { InboxRedirect } from "@/models/messaging/InboxRedirect";
import { MessagingAttachment } from "@/models/messaging/MessagingAttachment";
import { MessagingConversationsResponse } from "@/models/messaging/MessagingConversation";
import { MessagingMessage, MessagingMessagesResponse } from "@/models/messaging/MessagingMessage";
import { MessagingParticipant, MessagingParticipantsResponse } from "@/models/messaging/MessagingParticipant";

interface MessagingUnreadCountResponse {
  unread_count: number;
}

/**
 * Paging params for a messages read. `since` fetches messages strictly newer (live append);
 * `before` fetches the page immediately older than the cursor (upward infinite scroll). Mutually
 * exclusive in practice — pass one or neither (the latest page).
 */
export interface MessagesPageParams {
  since?: string;
  before?: string;
}

function buildMessagesQuery(opts?: MessagesPageParams): string {
  const params = new URLSearchParams();
  if (opts?.since) params.set("since", opts.since);
  if (opts?.before) params.set("before", opts.before);
  const query = params.toString();
  return query ? `?${query}` : "";
}

/** Params for a message search read: the term `q` plus the `before` cursor for backward paging. */
export interface MessageSearchParams {
  q: string;
  before?: string;
}

function buildSearchQuery(opts: MessageSearchParams): string {
  const params = new URLSearchParams();
  params.set("q", opts.q);
  if (opts.before) params.set("before", opts.before);
  return `?${params.toString()}`;
}

/** Builds the multipart body for a message send: optional `content` plus zero-or-more `files` parts. */
function buildMessageFormData(content: string, files: File[]): FormData {
  const formData = new FormData();
  if (content.length > 0) formData.append("content", content);
  files.forEach((f) => formData.append("files", f));
  return formData;
}

/** Posts a message as multipart/form-data — let the browser set the boundary; never hand-set Content-Type. */
function postMultipartMessage(path: string, content: string, files: File[]): Promise<ApiResponseV2<MessagingMessage>> {
  return HttpClientV2.post(path, buildMessageFormData(content, files), {
    axiosConfig: {
      timeout: 300000,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  });
}

/**
 * Client for the native LazoOne messaging inbox API. The KAM answers from Front; this service only
 * talks to Lazo's backend, which brokers to Front and stores the canonical history.
 */
class MessagingService {
  path = "/api/messages";

  getMessages(tenantExternalId: string, opts?: MessagesPageParams): Promise<ApiResponseV2<MessagingMessagesResponse>> {
    return HttpClientV2.get(`${this.path}/tenant/${tenantExternalId}${buildMessagesQuery(opts)}`);
  }

  sendMessage(tenantExternalId: string, content: string, files?: File[]): Promise<ApiResponseV2<MessagingMessage>> {
    const url = `${this.path}/tenant/${tenantExternalId}`;
    if (files && files.length > 0) {
      return postMultipartMessage(url, content, files);
    }
    return HttpClientV2.post(url, { content });
  }

  /** Full-history search of the client's KAM thread by message content (newest first). */
  searchMessages(tenantExternalId: string, opts: MessageSearchParams): Promise<ApiResponseV2<MessagingMessagesResponse>> {
    return HttpClientV2.get(`${this.path}/tenant/${tenantExternalId}/search${buildSearchQuery(opts)}`);
  }

  /**
   * Total messaging unread count for the authenticated user. Works for both the client and the KAM,
   * since the backend resolves the unread count from the caller's own conversations.
   */
  getUnreadCount(): Promise<ApiResponseV2<MessagingUnreadCountResponse>> {
    return HttpClientV2.get(`${this.path}/unread-count`);
  }

  /**
   * The cutover switch: whether this app's inbox screens should bounce to lazo-x, and where.
   * Runtime dynamic properties, so the cutover is a flip in the admin panel rather than a deploy.
   */
  getInboxRedirect(): Promise<ApiResponseV2<InboxRedirect>> {
    return HttpClientV2.get(`${this.path}/inbox-redirect`);
  }

  // ── Client multi-conversation inbox (LazoOne) ────────────────────────────
  // The client sees the list of conversations they own or were added to in their tenant, and opens
  // each by external id. All paths are tenant-scoped so a conversation from another company is
  // unreachable; the backend also access-checks each conversation.

  listClientConversations(
    tenantExternalId: string,
    status?: string,
    page = 0,
    size = 20
  ): Promise<ApiResponseV2<MessagingConversationsResponse>> {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    params.set("page", String(page));
    params.set("size", String(size));
    return HttpClientV2.get(`${this.path}/tenant/${tenantExternalId}/conversations?${params.toString()}`);
  }

  getClientConversationMessages(
    tenantExternalId: string,
    conversationExternalId: string,
    opts?: MessagesPageParams
  ): Promise<ApiResponseV2<MessagingMessagesResponse>> {
    return HttpClientV2.get(`${this.path}/tenant/${tenantExternalId}/conversations/${conversationExternalId}${buildMessagesQuery(opts)}`);
  }

  sendClientConversationMessage(
    tenantExternalId: string,
    conversationExternalId: string,
    content: string,
    files?: File[]
  ): Promise<ApiResponseV2<MessagingMessage>> {
    const url = `${this.path}/tenant/${tenantExternalId}/conversations/${conversationExternalId}`;
    if (files && files.length > 0) {
      return postMultipartMessage(url, content, files);
    }
    return HttpClientV2.post(url, { content });
  }

  searchClientConversationMessages(
    tenantExternalId: string,
    conversationExternalId: string,
    opts: MessageSearchParams
  ): Promise<ApiResponseV2<MessagingMessagesResponse>> {
    return HttpClientV2.get(
      `${this.path}/tenant/${tenantExternalId}/conversations/${conversationExternalId}/search${buildSearchQuery(opts)}`
    );
  }

  markClientConversationRead(tenantExternalId: string, conversationExternalId: string): Promise<ApiResponseV2<boolean>> {
    return HttpClientV2.post(`${this.path}/tenant/${tenantExternalId}/conversations/${conversationExternalId}/read`, {});
  }

  /** Active participants of a specific client conversation (read-only display). */
  getClientConversationParticipants(
    tenantExternalId: string,
    conversationExternalId: string
  ): Promise<ApiResponseV2<MessagingParticipantsResponse | MessagingParticipant[]>> {
    return HttpClientV2.get(`${this.path}/tenant/${tenantExternalId}/conversations/${conversationExternalId}/participants`);
  }

  // ── KAM inbox (agent side) ──────────────────────────────────────────────
  // The KAM sees every conversation assigned to them across all tenants, opens one, and replies.

  listConversations(status?: string, page = 0, size = 20): Promise<ApiResponseV2<MessagingConversationsResponse>> {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    params.set("page", String(page));
    params.set("size", String(size));
    return HttpClientV2.get(`${this.path}/conversations?${params.toString()}`);
  }

  getConversationMessages(conversationExternalId: string, opts?: MessagesPageParams): Promise<ApiResponseV2<MessagingMessagesResponse>> {
    return HttpClientV2.get(`${this.path}/conversations/${conversationExternalId}${buildMessagesQuery(opts)}`);
  }

  sendKamReply(conversationExternalId: string, content: string, files?: File[]): Promise<ApiResponseV2<MessagingMessage>> {
    const url = `${this.path}/conversations/${conversationExternalId}`;
    if (files && files.length > 0) {
      return postMultipartMessage(url, content, files);
    }
    return HttpClientV2.post(url, { content });
  }

  /**
   * Saves a message attachment into the tenant's Document Center under the given folder. When
   * {@code fileName} is provided, the file is renamed (the original extension is preserved
   * server-side); when omitted, the attachment's original name is kept.
   */
  saveAttachmentToDocumentCenter(
    conversationExternalId: string,
    attachmentExternalId: string,
    folderExternalId: string,
    fileName?: string
  ): Promise<ApiResponseV2<MessagingAttachment>> {
    return HttpClientV2.post(
      `${this.path}/conversations/${conversationExternalId}/attachments/${attachmentExternalId}/save-to-document-center`,
      { folderExternalId, fileName }
    );
  }

  /** Full-history search of a KAM conversation by message content (newest first). */
  searchConversationMessages(conversationExternalId: string, opts: MessageSearchParams): Promise<ApiResponseV2<MessagingMessagesResponse>> {
    return HttpClientV2.get(`${this.path}/conversations/${conversationExternalId}/search${buildSearchQuery(opts)}`);
  }

  markConversationRead(conversationExternalId: string): Promise<ApiResponseV2<boolean>> {
    return HttpClientV2.post(`${this.path}/conversations/${conversationExternalId}/read`, {});
  }

  // ── Ops participants (add/remove members of a conversation) ──────────────

  /**
   * Current participants of a conversation. The backend may return either a wrapped
   * `{ participants: TinyUser[] }` envelope or a bare `TinyUser[]`; both are accepted here.
   */
  getParticipants(conversationExternalId: string): Promise<ApiResponseV2<MessagingParticipantsResponse | MessagingParticipant[]>> {
    return HttpClientV2.get(`${this.path}/conversations/${conversationExternalId}/participants`);
  }

  addParticipants(conversationExternalId: string, userExternalIds: string[], seeFullHistory = false): Promise<ApiResponseV2<boolean>> {
    return HttpClientV2.post(`${this.path}/conversations/${conversationExternalId}/participants`, { userExternalIds, seeFullHistory });
  }

  removeParticipant(conversationExternalId: string, userExternalId: string): Promise<ApiResponseV2<boolean>> {
    return HttpClientV2.delete(`${this.path}/conversations/${conversationExternalId}/participants/${userExternalId}`);
  }

  /** The caller leaves a conversation they were invited to (portal side). Self-service: no manage rights needed. */
  leaveConversation(conversationExternalId: string): Promise<ApiResponseV2<boolean>> {
    return HttpClientV2.delete(`${this.path}/conversations/${conversationExternalId}/participants/me`);
  }

  // ── Client participants (tenant-scoped, LazoOne) ─────────────────────────
  // The client manages the participants of their own KAM thread: the primary client (thread owner)
  // may add/remove client contacts of the same company. The backend rejects cross-company users and
  // internal Lazo users on this path.

  /** Active participants of the caller's thread (primary client + client contacts + any Ops). */
  getClientParticipants(tenantExternalId: string): Promise<ApiResponseV2<MessagingParticipantsResponse | MessagingParticipant[]>> {
    return HttpClientV2.get(`${this.path}/tenant/${tenantExternalId}/participants`);
  }

  /** Adds client contacts of the same company to the caller's thread. */
  addClientContactParticipants(
    tenantExternalId: string,
    userExternalIds: string[],
    seeFullHistory = false
  ): Promise<ApiResponseV2<boolean>> {
    return HttpClientV2.post(`${this.path}/tenant/${tenantExternalId}/participants`, { userExternalIds, seeFullHistory });
  }

  /** Removes a client contact from the caller's thread. */
  removeClientContactParticipant(tenantExternalId: string, userExternalId: string): Promise<ApiResponseV2<boolean>> {
    return HttpClientV2.delete(`${this.path}/tenant/${tenantExternalId}/participants/${userExternalId}`);
  }

  /** The caller leaves a conversation they were invited to (client side). Self-service: no manage rights needed. */
  leaveClientConversation(tenantExternalId: string, conversationExternalId: string): Promise<ApiResponseV2<boolean>> {
    return HttpClientV2.delete(`${this.path}/tenant/${tenantExternalId}/conversations/${conversationExternalId}/participants/me`);
  }
}

const messagingService = new MessagingService();
export default messagingService;
